import { useState } from "react";
import Button from "@mui/material/Button";
import {
  getAuth,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut,
} from "firebase/auth";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../api";
import SuccessModal from "../../../components/Modal/SuccessModal";

const getStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]|[^a-zA-Z0-9]/.test(password)) score++;
  return score;
};

const strengthConfig = {
  0: { label: "", color: "#e0e0e0" },
  1: { label: "Weak", color: "#e53935" },
  2: { label: "Fair", color: "#fb8c00" },
  3: { label: "Good", color: "#43a047" },
  4: { label: "Strong", color: "#006663" },
};

const ChangePassword = () => {
  const [form, setForm] = useState({ current: "", newPass: "", confirm: "" });
  const [show, setShow] = useState({
    current: false,
    newPass: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const strength = getStrength(form.newPass);
  const config = strengthConfig[strength];
  const { user } = useAuth();

  const requirements = [
    { label: "At least 8 characters", met: form.newPass.length >= 8 },
    {
      label: "Contains uppercase letter (A-Z)",
      met: /[A-Z]/.test(form.newPass),
    },
    {
      label: "Contains lowercase letter (a-z)",
      met: /[a-z]/.test(form.newPass),
    },
    {
      label: "Contains number (0-9) or special character",
      met: /[0-9]|[^a-zA-Z0-9]/.test(form.newPass),
    },
  ];

  const toggleShow = (field) =>
    setShow((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPass !== form.confirm)
      return toast.error("Passwords do not match");
    if (form.newPass.length < 8)
      return toast.error("Password must be at least 8 characters");

    setLoading(true);
    try {
      await api.patch("/auth/change-password", {
        currentPassword: form.current,
        newPassword: form.newPass,
      });
      setForm({ current: "", newPass: "", confirm: "" });
      setShowSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = async () => {
    const auth = getAuth();
    await signOut(auth);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    window.location.href = "/login";
  };

  return (
    <div className="profile-card">
      <div className="profile-card-header">
        <div>
          <h3>Change Password</h3>
          <p>Update your password to keep your account secure.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="password-form">
        {/* Current Password */}
        <div className="field-group">
          <label className="field-label">Current Password</label>
          <div className="input-wrapper">
            <input
              type={show.current ? "text" : "password"}
              className="field-input"
              placeholder="Enter current password"
              value={form.current}
              onChange={(e) =>
                setForm((p) => ({ ...p, current: e.target.value }))
              }
              required
            />
            <span
              className="input-eye material-symbols-outlined"
              onClick={() => toggleShow("current")}
            >
              {show.current ? "visibility_off" : "visibility"}
            </span>
          </div>
        </div>

        {/* New Password */}
        <div className="field-group">
          <label className="field-label">New Password</label>
          <div className="input-wrapper">
            <input
              type={show.newPass ? "text" : "password"}
              className="field-input"
              placeholder="Enter new password"
              value={form.newPass}
              onChange={(e) =>
                setForm((p) => ({ ...p, newPass: e.target.value }))
              }
              required
            />
            <span
              className="input-eye material-symbols-outlined"
              onClick={() => toggleShow("newPass")}
            >
              {show.newPass ? "visibility_off" : "visibility"}
            </span>
          </div>

          {/* Strength bar */}
          {form.newPass && (
            <>
              <div className="strength-bar-row">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="strength-segment"
                    style={{
                      background: i <= strength ? config.color : "#e0e0e0",
                      transition: "background 0.3s ease",
                    }}
                  />
                ))}
                <span
                  className="strength-label"
                  style={{ color: config.color }}
                >
                  {config.label}
                </span>
              </div>

              {/* Requirements */}
              <div className="requirements-grid">
                {requirements.map((req) => (
                  <div key={req.label} className="requirement-item">
                    <span
                      className="material-symbols-outlined requirement-icon"
                      style={{ color: req.met ? "#006663" : "#ccc" }}
                    >
                      check_circle
                    </span>
                    <span
                      className="requirement-label"
                      style={{ color: req.met ? "#3e4944" : "#aaa" }}
                    >
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Confirm Password */}
        <div className="field-group">
          <label className="field-label">Confirm New Password</label>
          <div className="input-wrapper">
            <input
              type={show.confirm ? "text" : "password"}
              className="field-input"
              placeholder="Re-enter new password"
              value={form.confirm}
              onChange={(e) =>
                setForm((p) => ({ ...p, confirm: e.target.value }))
              }
              required
            />
            <span
              className="input-eye material-symbols-outlined"
              onClick={() => toggleShow("confirm")}
            >
              {show.confirm ? "visibility_off" : "visibility"}
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="password-form-footer">
          <p className="last-changed">
            <span className="material-symbols-outlined">schedule</span>
            Password last changed:{" "}
            <strong>
              {user?.passwordLastChanged
                ? new Date(user.passwordLastChanged).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Never"}
            </strong>
          </p>
          <div className="footer-actions">
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setForm({ current: "", newPass: "", confirm: "" })}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </div>
        </div>
      </form>
      <SuccessModal
        open={showSuccess}
        icon="check_circle"
        title="Password Changed!"
        message="Your password has been updated successfully. Please log in again with your new password."
        buttonText="Log in Again"
        onClose={handleSuccessClose}
      />
    </div>
  );
};

export default ChangePassword;
